
/// <amd-module name="Wit/DeliveryPlans/Common/Components/TeamSettingsTable/DeliveryPlansTeamSettingsService" />
import { getTFSPageData } from "Tfs/Platform/Page";
import { ITeamProject, ITfsProjectService } from "TFS/Services/TfsProjectService";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, newGuid } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { TeamBacklogMapping } from "Wit/Clients/Work/Contracts/Work";
import { DropdownOption, ProjectTeamsData, TeamsDataRequestParams } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import { ITeamSelectedSettingData, ITeamSettingData } from "Wit/DeliveryPlans/Common/Components/TeamSettingsTable/TeamSettingsInterfaces";
import * as Resources from "Wit/DeliveryPlans/Resources";

namespace TeamSettingsDataProviders {
    export const TeamsDataProvider = "ms.vss-work-web.teams-data-provider";
    export const ProjectsDataProvider = "ms.vss-tfs-web.project-plus-product-data-provider";
}

export function getTeamSettingsAsTeamBacklogMapping(teams: ITeamSelectedSettingData[]): TeamBacklogMapping[] {
    return teams.map(setting => {
        return {
            teamId: setting.team,
            categoryReferenceName: setting.backlogLevel
        };
    });
}

/**
 * Manage state and data for TeamSettingsTable
 */
export interface IDeliveryPlansTeamSettingsService extends IVssService {
    /**
     * Error message from data provider failure
     */
    dataProviderErrorMessage: IObservableValue<string | undefined>;

    /**
     * Tracks if data has changed if teams were initially supplied
     */
    isDirty: IObservableValue<boolean>;

    /**
     * Tracks if data is valid
     */
    isValid: IObservableValue<boolean>;

    /**
     * Indicates when initial project load is completed
     */
    projectsReady: IObservableValue<boolean>;

    /**
     * Observable list of configured team settings
     */
    rows: IObservableArray<ITeamSettingData>;

    /**
     * Error message from invalid team settings
     */
    validationErrorMessage: IObservableValue<string | undefined>;

    /**
     * Add a new team settings row with existing data or default values
     */
    addTeamSettingsRow(data?: ITeamSelectedSettingData): void;

    /**
     * Add existing team settings rows
     *
     * Note: this is intended to be used a single time when the component is passed existing team data
     */
    addTeamSettingsRows(teams: ITeamSelectedSettingData[]): void;

    /**
     * Clear the current team settings
     */
    clear(): void;

    /**
     * Get a friendly string for a team setting
     */
    getFriendlyTeamSettingString(setting: ITeamSettingData): string | undefined;

    /**
     * Move the supplied setting data to a new index
     */
    moveRow(setting: ITeamSettingData, index: number): void;

    /**
     * Remove a team settings entry
     */
    removeTeamSettingsRow(index: number): void;

    /**
     * Update the project of a team setting
     */
    updateProject(index: number, projectId: string, preserveSelection?: boolean): void;

    /**
     * Update the team of a team setting
     */
    updateTeam(index: number, teamId: string, preserveSelection?: boolean): void;

    /**
     * Update the backlog of a team setting
     */
    updateBacklog(index: number, backlogId: string): void;
}

class DeliveryPlansTeamSettingsService extends VssService implements IDeliveryPlansTeamSettingsService {
    private _dataProviderErrorMessage: IObservableValue<string | undefined>;
    private _isDirty: IObservableValue<boolean>;
    private _isValid: IObservableValue<boolean>;
    private _originalTeams: ITeamSelectedSettingData[] | undefined;
    private _projectId: string;
    private _projects: IObservableArray<IListBoxItem>;
    private _projectsReady: IObservableValue<boolean>;
    private _rows: IObservableArray<ITeamSettingData>;
    private _teams: { [projectId: string]: DropdownOption[] };
    private _teamBacklogs: { [teamId: string]: DropdownOption[] };
    private _validationErrorMessage: IObservableValue<string | undefined>;

    public get dataProviderErrorMessage(): IObservableValue<string | undefined> {
        return this._dataProviderErrorMessage;
    }

    public get isDirty(): IObservableValue<boolean> {
        return this._isDirty;
    }

    public get isValid(): IObservableValue<boolean> {
        return this._isValid;
    }

    public get projectsReady(): IObservableValue<boolean> {
        return this._projectsReady;
    }

    public get rows(): IObservableArray<ITeamSettingData> {
        return this._rows;
    }

    public get validationErrorMessage(): IObservableValue<string | undefined> {
        return this._validationErrorMessage;
    }

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this._projectId = getTFSPageData(pageContext)!.project!.id;

        this._dataProviderErrorMessage = new ObservableValue<string | undefined>(undefined);
        this._isDirty = new ObservableValue<boolean>(false);
        this._isValid = new ObservableValue<boolean>(true);
        this._projects = new ObservableArray([loadingItem]);
        this._rows = new ObservableArray([]);
        this._teams = {};
        this._teamBacklogs = {};
        this._validationErrorMessage = new ObservableValue<string | undefined>("");

        this._loadProjects();
        this._loadTeams(this._projectId);
    }

    public async addTeamSettingsRow(data?: ITeamSelectedSettingData) {
        const newRow = this._getDefaultSettings(data);
        this._rows.push(newRow);
        const index = this._rows.length - 1;

        const updateProjectSelection = () => {
            // update selections
            const projectIndex = newRow.projects.value.findIndex(p => p.id === newRow.project);
            newRow.projectSelection.select(projectIndex);

            // unsubscribe so we can guarantee that this callback will only run once
            this._projectsReady.unsubscribe(updateProjectSelection);
        };

        // update the project selection index or add a callback for when they are loaded
        if (!this._projectsReady.value) {
            this._projectsReady.subscribe(updateProjectSelection);
        } else {
            updateProjectSelection();
        }

        // update teams and backlogs data for the default project
        await this.updateProject(index, newRow.project, !!data);
    }

    public async addTeamSettingsRows(teams: ITeamSelectedSettingData[]) {
        // save teams for dirty comparisons
        this._originalTeams = teams;
        teams.forEach(team => this.addTeamSettingsRow(team));
    }

    public clear() {
        this._rows.value = [];
        this._validationErrorMessage.value = undefined;
        this._dataProviderErrorMessage.value = undefined;
        this._isValid.value = true;
        this._isDirty.value = false;
    }

    public getFriendlyTeamSettingString(setting: ITeamSettingData): string | undefined {
        const team = this._teams[setting.project].find(t => t.id === setting.team);
        if (team) {
            const backlog = this._teamBacklogs[team.id].find(b => b.id === setting.backlogLevel);
            if (backlog) {
                return team.name + " " + backlog.name;
            }
        }

        return team?.name;
    }

    public moveRow(setting: ITeamSettingData, index: number) {
        const rows = this._rows;
        const currentIndex = rows.value.findIndex(r => r.id === setting.id);
        rows.splice(currentIndex, 1);
        rows.splice(index, 0, setting);

        this._setIsDirty();
    }

    public removeTeamSettingsRow(rowIndex: number) {
        const rows = this._rows.value;
        rows.splice(rowIndex, 1);
        this._rows.value = rows;
        this._validateTeamSettings();
    }

    public async updateProject(index: number, projectId: string, preserveSelection?: boolean) {
        const teamSettingsData = this._rows.value[index];
        teamSettingsData.project = projectId;

        // clear teams and backlogs selections
        teamSettingsData.teamSelection.clear();
        teamSettingsData.backlogSelection.clear();

        // update teams and backlogs dropdowns
        teamSettingsData.ready.value = false;
        teamSettingsData.teams.value = [loadingItem];
        teamSettingsData.backlogLevels.value = [loadingItem];
        const teamsInProject = await this._getTeams(projectId);

        // in the case where we are loading existing teams, keep the currently selected team
        let indexToSelect = 0;
        if (preserveSelection) {
            const teamIndex = teamsInProject.findIndex(team => team.id === teamSettingsData.team);
            indexToSelect = teamIndex >= 0 ? teamIndex : indexToSelect;
        }

        const team = teamsInProject[indexToSelect];
        this.updateTeam(index, team.id, /** skipValidation */ true, preserveSelection);
        teamSettingsData.teams.value = this._getListBoxItems(teamsInProject);
        teamSettingsData.teamSelection.select(indexToSelect);
        teamSettingsData.ready.value = true;

        this._validateTeamSettings();
    }

    public updateTeam(index: number, teamId: string, skipValidation?: boolean, preserveSelection?: boolean) {
        const teamSettingsData = this._rows.value[index];
        teamSettingsData.team = teamId;
        const backlogs = this._teamBacklogs[teamId];

        // in the case where we are loading existing teams, keep the currently selected backlog
        let indexToSelect = -1;
        if (preserveSelection) {
            const backlogIndex = backlogs.findIndex(backlog => backlog.id === teamSettingsData.backlogLevel);
            indexToSelect = backlogIndex >= 0 ? backlogIndex : indexToSelect;
        }

        teamSettingsData.backlogLevels.value = this._getListBoxItems(backlogs);

        // either update the selection or clear the backlog value
        if (indexToSelect >= 0) {
            teamSettingsData.backlogSelection.select(indexToSelect);
        } else {
            this.updateBacklog(index, "", /** skipValidation */ true);
        }

        !skipValidation && this._validateTeamSettings();
    }

    public updateBacklog(index: number, backlogId: string, skipValidation?: boolean) {
        const teamSettingsData = this._rows.value[index];
        teamSettingsData.backlogLevel = backlogId;
        !skipValidation && this._validateTeamSettings();
    }

    private async _getTeams(projectId: string): Promise<DropdownOption[]> {
        const teams = this._teams[projectId];
        if (!teams) {
            return await this._loadTeams(projectId);
        } else {
            return teams;
        }
    }

    private _validateTeamSettings() {
        const settings = this._rows.value;

        if (!this._validateUniqueTeamSettings(settings)) {
            this._validationErrorMessage.value = Resources.DuplicateBacklogsErrorMessage;
        }
        // check that there are no empty selections
        else if (settings.findIndex(s => !s.project || !s.team || !s.backlogLevel) >= 0) {
            // empty to show that the model isn't valid, but there is no error message to display
            this._validationErrorMessage.value = "";
        }
        // otherwise clear error message if necessary
        else if (this._validationErrorMessage.value !== undefined) {
            this._validationErrorMessage.value = undefined;
        }

        this._setIsValid();
        this._setIsDirty();
    }

    private _validateUniqueTeamSettings(settings: ITeamSettingData[]): boolean {
        // check for any duplicate settings
        const uniqueTeamSettings: { [teamId: string]: string[] } = {};
        for (let i = 0; i < settings.length; i++) {
            const setting = settings[i];
            if (!uniqueTeamSettings[setting.team]) {
                uniqueTeamSettings[setting.team] = [];
            } else if (setting.backlogLevel && uniqueTeamSettings[setting.team].indexOf(setting.backlogLevel) >= 0) {
                // found a duplicate team and backlog
                return false;
            }

            uniqueTeamSettings[setting.team].push(setting.backlogLevel);
        }

        return true;
    }

    private _getDefaultSettings(data?: ITeamSelectedSettingData): ITeamSettingData {
        const project = this._projectId;
        const id = newGuid();

        const teamSettingsData = {
            id,
            project: data?.project || project,
            team: data?.team || "",
            backlogLevel: data?.backlogLevel || "",
            projects: this._projects,
            teams: new ObservableArray([loadingItem]),
            backlogLevels: new ObservableArray([loadingItem]),
            projectSelection: new DropdownSelection(),
            teamSelection: new DropdownSelection(),
            backlogSelection: new DropdownSelection(),
            ready: new ObservableValue(false)
        };

        return teamSettingsData;
    }

    private _loadProjects() {
        const projectsService = this.pageContext.getService<ITfsProjectService>("ITfsProjectService");
        const projects = projectsService.getAllProjects();
        this._projectsReady = projects.ready;
        projects.ready.subscribe(ready => {
            if (ready) {
                const projectsList = projects.value;
                this._projects.value = this._getListBoxItemsFromProjects(projectsList);
                if (projectsList.length === 0) {
                    this._handleDataProviderError(TeamSettingsDataProviders.ProjectsDataProvider);
                }
            }
        });
    }

    private async _loadTeams(projectId: string): Promise<DropdownOption[]> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const requestParams: TeamsDataRequestParams = { projectId };
        const teamsData = await contributionService.getDataAsync<ProjectTeamsData>(TeamSettingsDataProviders.TeamsDataProvider, requestParams);

        if (!teamsData) {
            this._handleDataProviderError(TeamSettingsDataProviders.TeamsDataProvider);
            return [];
        } else {
            this._addBacklogLevels(teamsData.backlogLevels);
            this._teams[projectId] = teamsData.teams;
            return teamsData.teams;
        }
    }

    private _getListBoxItems(options: DropdownOption[]): IListBoxItem[] {
        return options.map(option => {
            return { id: option.id, text: option.name } as IListBoxItem;
        });
    }

    private _getListBoxItemsFromProjects(options: ITeamProject[]): IListBoxItem[] {
        return options.map(option => {
            return { id: option.id, text: option.name } as IListBoxItem;
        });
    }

    private _handleDataProviderError(dataProviderName: string) {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[dataProviderName];
        if (exception) {
            this._dataProviderErrorMessage.value = format(Resources.DataProviderFailureErrorMessage, exception.message);
        }
    }

    private _addBacklogLevels(backlogLevels: { [key: string]: DropdownOption[] }) {
        for (const teamId of Object.keys(backlogLevels)) {
            this._teamBacklogs[teamId] = backlogLevels[teamId];
        }
    }

    private _setIsValid() {
        const valid = this._validationErrorMessage.value === undefined;

        if (this._isValid.value !== valid) {
            this._isValid.value = valid;
        }
    }

    private _setIsDirty() {
        if (!this._originalTeams) {
            return;
        }

        const currentTeams = this._rows.value as ITeamSelectedSettingData[];
        let dirty = this._originalTeams.length != currentTeams.length;
        if (!dirty) {
            dirty = this._originalTeams.findIndex((team, index) => !areTeamsEqual(team, currentTeams[index])) >= 0;
        }

        if (this._isDirty.value !== dirty) {
            this._isDirty.value = dirty;
        }
    }
}

function areTeamsEqual(a: ITeamSelectedSettingData, b: ITeamSelectedSettingData): boolean {
    return a.backlogLevel === b.backlogLevel && a.project === b.project && a.team === b.team;
}

const loadingItem: IListBoxItem = {
    id: "loading",
    type: ListBoxItemType.Loading
};

const serviceName = "IDeliveryPlansTeamSettingsService";
Services.add(serviceName, { serviceFactory: DeliveryPlansTeamSettingsService });
